import { useState, useEffect } from 'react';
import { getSTAI2ProjectsWithLocations, isProvinceAllowedForSTAI2 } from '../VideoCall/stai2Service';

/**
 * Hook per gestire la disponibilità di STAI2 basata sulle province autorizzate dal backend
 *
 * LOGICA SEMPLIFICATA:
 * - Se locations_allowed è null/vuoto → Servizio sempre disponibile
 * - Se locations_allowed ha province specifiche → Richiede geolocalizzazione
 * - Se geolocalizzazione fallisce ma ci sono restrizioni → Nega accesso (sicurezza)
 *
 * @param {string} userProvince - Codice provincia dell'utente (es: 'MI', 'RM')
 * @returns {object} - { isAvailable, allowedProvinces, loading, error, checkResult }
 */
export const useSTAI2Availability = (userProvince = null) => {
  const [allowedProvinces, setAllowedProvinces] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  // Load STAI2 projects and locations on mount
  useEffect(() => {
    const loadSTAI2Projects = async () => {
      try {
        setLoading(true);
        setError(null);

        const projects = await getSTAI2ProjectsWithLocations();
        const stai2Project = projects.find(p => p.code === 'STAI2');

        setAllowedProvinces(stai2Project?.locations_allowed || null);

        // Check if project is active
        if (stai2Project && stai2Project.is_active === false) {
          console.log('🚫 STAI2 - Project is inactive, service not available');
          setAllowedProvinces(null);
          setError(new Error('Project inactive'));
          return;
        }

        console.log('✅ STAI2 - Project loaded and active:', stai2Project?.name);

      } catch (err) {
        console.error('❌ STAI2 - Error loading projects:', err);
        setError(err);
        setAllowedProvinces(null); // Fallback: allow all provinces
      } finally {
        setLoading(false);
      }
    };

    loadSTAI2Projects();
  }, []);

  // Check province availability when userProvince changes
  useEffect(() => {
    if (!userProvince || loading) {
      setCheckResult(null);
      return;
    }

    const checkProvince = async () => {
      try {
        const result = await isProvinceAllowedForSTAI2(userProvince);
        setCheckResult(result);
      } catch (err) {
        console.error('❌ STAI2 - Error checking province:', err);
        setCheckResult({
          allowed: true,
          reason: 'error_fallback'
        });
      }
    };

    checkProvince();
  }, [userProvince, allowedProvinces, loading]);

  /**
   * Check if a specific province is allowed
   * @param {string} province - Province code to check
   * @returns {boolean}
   */
  const isAvailable = (province = userProvince) => {
    // No restrictions = all provinces allowed
    if (!allowedProvinces || !Array.isArray(allowedProvinces) || allowedProvinces.length === 0) {
      return true;
    }

    // No province = cannot verify, deny for security
    if (!province) {
      return false;
    }

    // Check if province is in allowed list (expecting IT-XX format)
    return allowedProvinces.some(loc => loc && loc.code === province);
  };

  /**
   * Get allowed province codes array
   * @returns {string[]|null} Array of province codes or null if all allowed
   */
  const getAllowedProvinceCodes = () => {
    if (!allowedProvinces || allowedProvinces.length === 0) return null;
    return allowedProvinces.map(loc => loc.code);
  };

  /**
   * Get allowed province names array
   * @returns {string[]|null} Array of province names or null if all allowed
   */
  const getAllowedProvinceNames = () => {
    if (!allowedProvinces || allowedProvinces.length === 0) return null;
    return allowedProvinces.map(loc => loc.name);
  };

  return {
    isAvailable: (() => {
      // If there's an error (like project inactive), deny access
      if (error) {
        return false;
      }

      // Use check result if available
      if (checkResult) {
        return checkResult.allowed;
      }

      // If no restrictions (locations_allowed is null/empty), allow access
      if (!allowedProvinces || allowedProvinces.length === 0) {
        return true;
      }

      // If there are restrictions but no user province detected, deny access (security)
      if (!userProvince) {
        return false;
      }

      // Check if user province is in allowed list
      return isAvailable(userProvince);
    })(),
    allowedProvinces,
    getAllowedProvinceCodes,
    getAllowedProvinceNames,
    loading,
    error,
    checkResult,
    // Helper methods
    isAllProvincesAllowed: !allowedProvinces || allowedProvinces.length === 0,
    hasRestrictions: allowedProvinces && allowedProvinces.length > 0
  };
};

export default useSTAI2Availability;
